// AiFeedbackControls.jsx — upvote / downvote for Ai replies

import React, { useCallback, useState } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Context
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';

// Utilities
import { resolveAiReplyText } from 'spaces/utils/aiFeedbackUtils';
import { getPersonName } from 'utilities/PersonIdentity';

// CSS
import './AiFeedbackControls.css';

// Components
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalButtonSection from 'components/modal/buttonsection/ModalButtonSection';
import ModalField from 'components/modal/field/ModalField';
import ModalForm from 'components/modal/form/ModalForm';
import ModalLabel from 'components/modal/label/ModalLabel';
import Modal from 'components/modal/Modal';
import TextInput from 'components/textinput/TextInput';
import { submitAiFeedback } from 'spaces/services/AiFeedbackService';

const AiFeedbackControls = React.memo(({
  message,
  promptText = '',
}) => {

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- CONTEXT ----- //

  const { theme } = useTheme();
  const { user } = useUser();

  // ----- STATE ----- //

  const [pendingVote, setPendingVote] = useState(null);
  const [selectedVote, setSelectedVote] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const modalOpen = Boolean(pendingVote);

  // ----- MEMOS ----- //

  const closeModal = useCallback(() => {
    if (submitting) return;
    setPendingVote(null);
    setComment('');
    setError('');
  }, [submitting]);

  const openModal = useCallback((vote) => {
    setPendingVote(vote);
    setComment('');
    setError('');
  }, []);

  // ----- HANDLERS ----- //

  const handleSubmit = useCallback(async () => {
    const cleanUserKey = String(user?.key || '').trim();
    const cleanMessageKey = String(message?.key || '').trim();
    const cleanBaseKey = String(message?.baseKey || '').trim();
    const cleanChannelKey = String(message?.channelKey || '').trim();

    if (!cleanUserKey || !cleanMessageKey || !cleanBaseKey || !cleanChannelKey || !pendingVote) {
      setError('Unable to submit feedback for this message.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await submitAiFeedback({
        messageKey: cleanMessageKey,
        baseKey: cleanBaseKey,
        channelKey: cleanChannelKey,
        messageType: String(message?.messageType || '').trim(),
        vote: pendingVote,
        comment,
        promptText,
        replyText: resolveAiReplyText(message),
        userKey: cleanUserKey,
        userName: getPersonName(user, 'Member'),
      });
      setSelectedVote(pendingVote);
      setPendingVote(null);
      setComment('');
    } catch (submitError) {
      setError(submitError?.message || 'Failed to submit feedback.');
    } finally {
      setSubmitting(false);
    }
  }, [comment, message, pendingVote, promptText, user]);

  if (!message?.key) return null;

  const upActive = selectedVote === 'up';
  const downActive = selectedVote === 'down';

  // ----- RENDER ----- //

  return (
    <>
      <div className="ai-feedback-controls">
        <Button
          className="ai-feedback-controls__button"
          title="Upvote"
          iconKey="thumb_up"
          iconColor={upActive ? theme.foreground : theme.foregroundMuted}
          backgroundColor="transparent"
          hoverForegroundColor={theme.foreground}
          iconSize="14"
          height="28px"
          width="28px"
          padding="4px"
          borderRadius="6px"
          onClick={() => openModal('up')}
        />
        <Button
          className="ai-feedback-controls__button"
          title="Downvote"
          iconKey="thumb_down"
          iconColor={downActive ? theme.foreground : theme.foregroundMuted}
          backgroundColor="transparent"
          hoverForegroundColor={theme.foreground}
          iconSize="14"
          height="28px"
          width="28px"
          padding="4px"
          borderRadius="6px"
          onClick={() => openModal('down')}
        />
      </div>

      <Modal
        iconKey={pendingVote === 'down' ? 'thumb_down' : 'thumb_up'}
        title={pendingVote === 'down' ? 'Downvote reply' : 'Upvote reply'}
        isOpen={modalOpen}
        width={isMobile ? '100%' : 480}
        maxHeight={520}
        onClose={closeModal}
      >
        <ModalForm gap="16px">
          <ModalField>
            <ModalLabel label="Optional details" />
            <TextInput
              value={comment}
              placeholder={pendingVote === 'down' ? 'What went wrong?' : 'What worked?'}
              multiline
              expandable
              maxHeight={180}
              disabled={submitting}
              onChange={(event) => setComment(event.target.value)}
            />
          </ModalField>

          {/* ERROR */}
          {error ? (
            <div
              className="ai-feedback-controls__error"
              style={{
                color: theme.danger || '#c44',
              }}
            >
              {error}
            </div>
          ) : null}
        </ModalForm>
        <ModalButtons>
          <ModalButtonSection align="END">
            <Button
              label="Cancel"
              enabled={!submitting}
              backgroundColor="transparent"
              labelColor={theme.foregroundMuted}
              onClick={closeModal}
            />
            <Button
              label={submitting ? 'Submitting…' : 'Submit'}
              enabled={!submitting}
              onClick={handleSubmit}
            />
          </ModalButtonSection>
        </ModalButtons>
      </Modal>
    </>
  );
});

// Dev Tools
AiFeedbackControls.displayName = 'AiFeedbackControls';

export default AiFeedbackControls;
